/**
 * Shared UI primitives. Pages should compose these (plus the utility classes in styles.css)
 * instead of inventing their own styling so the app stays visually consistent.
 */
import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { Icon, type IconName } from './icons';
import { parseNum } from '@/lib/format';

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}
export { cx };

// ---------------------------------------------------------------- Announcements and page titles
let announcer: HTMLElement | null = null;
let announceTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Reads `message` to screen readers politely without moving focus (an armed ConfirmButton, a page change). One shared
 * live region, added to the page on first use.
 */
export function announce(message: string) {
  if (typeof document === 'undefined') return;
  if (!announcer?.isConnected) {
    announcer = document.createElement('div');
    announcer.className = 'sr-only';
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');
    document.body.appendChild(announcer);
  }
  const region = announcer;
  region.textContent = '';
  clearTimeout(announceTimer);
  // A beat after the region exists and is empty, so the change is read, a repeat of the last message too.
  announceTimer = setTimeout(() => {
    region.textContent = message;
  }, 100);
}

/**
 * Puts focus back once something that asked (an AI suggestion, a recap) is done. The button that asked is disabled
 * while it asks, or gone once the answer shows, and the browser drops focus to the page (out of a dialog, too). When
 * `busy` ends with focus lost like that, it moves to `target()`: the answer, or the button to try again. Focus the user
 * put somewhere else stays there.
 */
export function useRefocusWhenDone(busy: boolean, target: () => HTMLElement | null | undefined) {
  const was = useRef(busy);
  const latest = useRef(target);
  latest.current = target;
  useEffect(() => {
    const ended = was.current && !busy;
    was.current = busy;
    if (!ended) return;
    const active = document.activeElement as HTMLElement | null;
    const lost = !active || active === document.body || !active.isConnected || (active as HTMLButtonElement).disabled === true;
    if (lost) latest.current()?.focus();
  }, [busy]);
}

/** "History · Taretto"; just "Taretto" without a page name. */
export function documentTitle(page?: string | null): string {
  return page ? `${page} · Taretto` : 'Taretto';
}

/**
 * Names the browser tab after something on the page (a bean, a method) instead of the route's own title, which Layout
 * sets from the route's `handle.title`. Pass null while the data loads to keep the route's.
 */
export function usePageTitle(title: string | null | undefined) {
  const { pathname } = useLocation();
  useEffect(() => {
    if (title) document.title = documentTitle(title);
  }, [title, pathname]);
}

// ---------------------------------------------------------------- Button
type ButtonVariant = 'default' | 'primary' | 'ghost' | 'danger' | 'success';
type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  iconRight?: IconName;
  block?: boolean;
  loading?: boolean;
}

export function Button({ variant = 'default', size = 'md', icon, iconRight, block, loading, className, children, disabled, type = 'button', ...rest }: ButtonProps) {
  const iconSize = size === 'sm' ? 16 : size === 'xl' ? 24 : 18;
  return (
    <button
      type={type}
      className={cx('btn', variant !== 'default' && variant, size !== 'md' && size, block && 'block', !children && 'icon', className)}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <span className="spinner" style={{ width: 16, height: 16 }} /> : icon && <Icon name={icon} size={iconSize} />}
      {children}
      {iconRight && <Icon name={iconRight} size={iconSize} />}
    </button>
  );
}

export function LinkButton({
  to,
  variant = 'default',
  size = 'md',
  icon,
  block,
  className,
  children,
  'aria-label': ariaLabel,
}: {
  to: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  block?: boolean;
  className?: string;
  children?: ReactNode;
  /** Needed when there are no children (an icon-only link). */
  'aria-label'?: string;
}) {
  const iconSize = size === 'sm' ? 16 : size === 'xl' ? 24 : 18;
  return (
    <Link to={to} aria-label={ariaLabel} className={cx('btn', variant !== 'default' && variant, size !== 'md' && size, block && 'block', !children && 'icon', className)}>
      {icon && <Icon name={icon} size={iconSize} />}
      {children}
    </Link>
  );
}

// ---------------------------------------------------------------- Card / layout
export function Card({ id, title, actions, children, className, onClick }: { id?: string; title?: ReactNode; actions?: ReactNode; children?: ReactNode; className?: string; onClick?: () => void }) {
  return (
    <section id={id} className={cx('card', onClick && 'interactive', className)} onClick={onClick}>
      {(title || actions) && (
        <div className="card-header">
          {title ? <h2 className="card-title">{title}</h2> : <span />}
          {actions && <div className="row">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/** `level` 2 is for a header inside a page that already has its h1 (the split view's detail pane); it looks the same. */
export function PageHeader({ title, subtitle, back, actions, level = 1 }: { title: ReactNode; subtitle?: ReactNode; back?: string; actions?: ReactNode; level?: 1 | 2 }) {
  const Heading = level === 1 ? 'h1' : 'h2';
  return (
    <header className="page-header">
      {back && <LinkButton to={back} variant="ghost" icon="chevron-left" aria-label="Back" />}
      <div className="titles">
        <Heading className="truncate">{title}</Heading>
        {subtitle && <div className="subtitle">{subtitle}</div>}
      </div>
      {actions && <div className="row-wrap">{actions}</div>}
    </header>
  );
}

export function EmptyState({ icon = 'coffee', title, children, action }: { icon?: IconName; title: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty-state">
      <Icon name={icon} size={40} className="icon" />
      <div className="bold">{title}</div>
      {children && <div className="text-sm">{children}</div>}
      {action}
    </div>
  );
}

export function Banner({ tone = 'info', icon, children }: { tone?: 'info' | 'warning' | 'danger' | 'success'; icon?: IconName; children: ReactNode }) {
  const defaultIcon: IconName = tone === 'success' ? 'check' : tone === 'info' ? 'info' : 'alert';
  return (
    <div className={cx('banner', tone)} role={tone === 'danger' ? 'alert' : undefined}>
      <Icon name={icon ?? defaultIcon} size={18} style={{ flexShrink: 0, marginTop: 1 }} />
      <div className="grow">{children}</div>
    </div>
  );
}

export function Spinner({ size = 20 }: { size?: number }) {
  return <span className="spinner" style={{ width: size, height: size, display: 'inline-block' }} />;
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="row muted" role="status" style={{ padding: 24, justifyContent: 'center' }}>
      <Spinner /> {label}
    </div>
  );
}

export function ErrorMessage({ error }: { error: unknown }) {
  const msg = error instanceof Error ? error.message : String(error);
  return <Banner tone="danger">{msg}</Banner>;
}

/**
 * Non-blocking notice for a failed background refetch. React Query keeps the last data when a refetch
 * fails, so pages keep rendering it (and any unsaved edits) and show this instead of a full-page error.
 * Use a full-page ErrorMessage only when there is no data at all.
 */
export function RefreshErrorBanner({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const msg = error instanceof Error ? error.message : String(error);
  return (
    <Banner tone="warning">
      <div className="row-wrap" style={{ justifyContent: 'space-between' }}>
        <span>
          Couldn’t refresh, so this may be out of date. <span className="muted">({msg})</span>
        </span>
        {onRetry && (
          <Button size="sm" variant="ghost" icon="refresh" onClick={onRetry}>
            Retry
          </Button>
        )}
      </div>
    </Banner>
  );
}

export function Badge({ tone, children }: { tone?: 'accent' | 'success' | 'warning' | 'danger'; children: ReactNode }) {
  return <span className={cx('badge', tone)}>{children}</span>;
}

export function Stat({ label, value, unit, hint }: { label: ReactNode; value: ReactNode; unit?: ReactNode; hint?: ReactNode }) {
  return (
    <div className="stat">
      <span className="label">{label}</span>
      <span className="value">
        {value}
        {unit && <small>{unit}</small>}
      </span>
      {hint && <span className="text-xs faint">{hint}</span>}
    </div>
  );
}

export function ProgressBar({ value, max = 1, tone }: { value: number; max?: number; tone?: 'success' | 'warning' | 'danger' }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div className={cx('progress', tone)} role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
      <div style={{ transform: `translateX(${pct - 100}%)` }} />
    </div>
  );
}

/**
 * Where an arrow key moves in a row of `count` items: on to the next or previous one (wrapping round), Home and End
 * to the ends. Up and Down count only for a group that isn't a single row (`twoAxes`). Null for any other key.
 */
export function rovingIndex(key: string, index: number, count: number, twoAxes = false): number | null {
  if (count <= 0) return null;
  if (key === 'ArrowRight' || (twoAxes && key === 'ArrowDown')) return (index + 1) % count;
  if (key === 'ArrowLeft' || (twoAxes && key === 'ArrowUp')) return (index - 1 + count) % count;
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  return null;
}

/** keydown for a group of tabs or radios: focus moves with the arrow keys and selects (a click) as it goes. */
function rovingKeyDown(role: 'tab' | 'radio') {
  return (e: ReactKeyboardEvent<HTMLElement>) => {
    const items = [...e.currentTarget.querySelectorAll<HTMLElement>(`[role="${role}"]`)].filter(
      (el) => !el.matches(':disabled') && el.getAttribute('aria-disabled') !== 'true',
    );
    const index = items.indexOf(document.activeElement as HTMLElement);
    const next = index < 0 ? null : rovingIndex(e.key, index, items.length, role === 'radio');
    if (next == null) return;
    e.preventDefault();
    items[next]!.focus();
    items[next]!.click();
  };
}

/**
 * Radio semantics for a group of tiles or buttons (the brew's method pills and drink tiles, a step's type): the group
 * is one Tab stop (the checked item, or the first), and the arrow keys move the choice. Spread `group` on the
 * container with an aria-label, and `radio(value)` on each item; an item that does more on click wraps `onClick`.
 *
 *   const { group, radio } = rovingRadio(method, setMethod, methods.map((m) => m.id));
 *   <div className="bw-methods" {...group} aria-label="Brew method">
 *     {methods.map((m) => <button type="button" key={m.id} className="chip" {...radio(m.id)}>…</button>)}
 *   </div>
 */
export function rovingRadio<T>(value: T | null | undefined, onChange: (v: T) => void, values: readonly T[]) {
  const tabStop = value != null && values.includes(value) ? value : values[0];
  return {
    group: { role: 'radiogroup' as const, onKeyDown: rovingKeyDown('radio') },
    radio: (v: T) => ({ role: 'radio' as const, 'aria-checked': v === value, tabIndex: v === tabStop ? 0 : -1, onClick: () => onChange(v) }),
  };
}

/**
 * A segmented row of tabs. `children` is the selected tab's content: it goes in the tabpanel the tabs control, with
 * the page's spacing. Without children the tabs only switch what follows (a filter). The row is one Tab stop; the
 * arrow keys, Home and End move along it and select.
 */
export function Tabs<T extends string>({
  value,
  onChange,
  options,
  label,
  children,
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: ReactNode }>;
  /** Names the row for screen readers ("Gear"). */
  label?: string;
  children?: ReactNode;
}) {
  const id = useId();
  const tabId = (v: T) => `${id}tab-${v}`;
  const panelId = `${id}panel`;
  const hasPanel = children !== undefined;
  const tabStop = options.some((o) => o.value === value) ? value : options[0]?.value;
  return (
    <>
      <div className="tabs" role="tablist" aria-label={label} onKeyDown={rovingKeyDown('tab')}>
        {options.map((o) => (
          <button
            key={o.value}
            id={tabId(o.value)}
            type="button"
            role="tab"
            aria-selected={o.value === value}
            aria-controls={hasPanel ? panelId : undefined}
            tabIndex={o.value === tabStop ? 0 : -1}
            className={o.value === value ? 'active' : undefined}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
      {hasPanel && (
        <div className="tab-panel" role="tabpanel" id={panelId} aria-labelledby={tabId(value)}>
          {children}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------- Form controls
/** The id of a Field's hint or error, for the control's aria-describedby. */
function messageId(id: string): string {
  return `${id}-msg`;
}

/**
 * aria-describedby and aria-invalid for the control inside a <Field htmlFor={id} hint error>, so a screen reader reads
 * the hint or error with the control. Spread it onto the control a Field wraps by hand.
 */
export function fieldAria(id: string, hint?: ReactNode, error?: ReactNode): { 'aria-describedby'?: string; 'aria-invalid'?: true } {
  return { 'aria-describedby': error || hint ? messageId(id) : undefined, 'aria-invalid': error ? true : undefined };
}

/** `labelId` gives the label an id, for a control named with aria-labelledby instead of `htmlFor` (the bean picker). */
export function Field({ label, hint, error, children, className, htmlFor, labelId }: { label?: ReactNode; hint?: ReactNode; error?: ReactNode; children: ReactNode; className?: string; htmlFor?: string; labelId?: string }) {
  const msgId = htmlFor ? messageId(htmlFor) : undefined;
  return (
    <div className={cx('field', className)}>
      {label && (
        <label htmlFor={htmlFor} id={labelId}>
          {label}
        </label>
      )}
      {children}
      {error ? (
        <span className="error" id={msgId}>
          {error}
        </span>
      ) : hint ? (
        <span className="hint" id={msgId}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

/**
 * A required field's label: the text and an asterisk drawn for the eye only. The control says it to screen readers
 * with `aria-required` (or `required`), so it isn't read as "Name star".
 */
export function Required({ children }: { children: ReactNode }) {
  return (
    <>
      {children} <span aria-hidden="true">*</span>
    </>
  );
}

export function TextInput({ label, hint, error, className, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label?: ReactNode; hint?: ReactNode; error?: ReactNode }) {
  const id = useId();
  return (
    <Field label={label} hint={hint} error={error} className={className} htmlFor={id}>
      <input id={id} className="input" {...fieldAria(id, hint, error)} {...rest} />
    </Field>
  );
}

export function TextArea({ label, hint, className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: ReactNode; hint?: ReactNode }) {
  const id = useId();
  return (
    <Field label={label} hint={hint} className={className} htmlFor={id}>
      <textarea id={id} className="textarea" {...fieldAria(id, hint)} {...rest} />
    </Field>
  );
}

export function Select<T extends string | number>({
  label,
  hint,
  className,
  value,
  onChange,
  options,
  placeholder,
  disabled,
  ...rest
}: {
  label?: ReactNode;
  hint?: ReactNode;
  className?: string;
  value: T | null;
  onChange: (v: T | null) => void;
  options: Array<{ value: T; label: string }>;
  /** When given, an empty option is shown that maps to null. */
  placeholder?: string;
  disabled?: boolean;
} & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'onChange'>) {
  const generatedId = useId();
  const id = rest.id ?? generatedId;
  return (
    <Field label={label} hint={hint} className={className} htmlFor={id}>
      <select
        {...fieldAria(id, hint)}
        {...rest}
        id={id}
        className="select"
        disabled={disabled}
        value={value == null ? '' : String(value)}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') return onChange(null);
          const opt = options.find((o) => String(o.value) === raw);
          onChange(opt ? opt.value : null);
        }}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={String(o.value)} value={String(o.value)}>
            {o.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

/**
 * Numeric input that keeps the user's raw text while typing (so "18." or "18,5" work)
 * and reports a parsed number (or null when empty/invalid).
 */
export function NumberInput({
  label,
  hint,
  error,
  className,
  value,
  onChange,
  unit,
  prefix,
  step,
  min,
  max,
  placeholder,
  decimals,
  format: formatValue,
  stepper,
  disabled,
  autoFocus,
  name,
  required,
  'aria-required': ariaRequired,
  'aria-label': ariaLabel,
}: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  className?: string;
  /** Plain-text name for the stepper buttons ("Decrease dose") when `label` isn't plain text, e.g. "dose". */
  name?: string;
  value: number | null;
  onChange: (v: number | null) => void;
  unit?: string;
  /** Shown before the number, e.g. "1:" for a ratio. */
  prefix?: string;
  step?: number;
  min?: number;
  max?: number;
  placeholder?: string;
  /** Decimals used when formatting values coming from outside (default: as-is). */
  decimals?: number;
  /** Formats values coming from outside instead of `decimals`. */
  format?: (v: number) => string;
  /** Show -/+ buttons (uses step, default 1). */
  stepper?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  /** The browser's own required check on submit, and screen readers hear "required". */
  required?: boolean;
  /** Only tells screen readers the field is required, for a form that checks it itself. */
  'aria-required'?: boolean;
  /** For an input without a visible label (rows of a list). */
  'aria-label'?: string;
}) {
  const id = useId();
  const format = useCallback(
    (v: number | null) => (v == null ? '' : formatValue ? formatValue(v) : decimals != null ? v.toFixed(decimals) : String(v)),
    [decimals, formatValue],
  );
  const [text, setText] = useState(() => format(value));
  const lastEmitted = useRef<number | null>(value);

  // Sync external value changes (but not while the user's text already represents it).
  useEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      setText(format(value));
    }
  }, [value, format]);

  const emit = (n: number | null) => {
    lastEmitted.current = n;
    onChange(n);
  };
  const clamp = (n: number) => Math.min(max ?? Infinity, Math.max(min ?? -Infinity, n));
  const bump = (dir: 1 | -1) => {
    const s = step ?? 1;
    const base = value ?? min ?? 0;
    const next = clamp(Math.round((base + dir * s) / s) * s);
    const fixed = Number(next.toFixed(6));
    setText(format(fixed));
    emit(fixed);
  };

  // "Decrease dose" rather than four identical "Decrease" buttons on one form.
  const stepName = name ?? (typeof label === 'string' ? label : ariaLabel)?.toLowerCase();

  const input = (
    <input
      id={id}
      className="input"
      inputMode="decimal"
      value={text}
      placeholder={placeholder}
      disabled={disabled}
      autoFocus={autoFocus}
      required={required}
      aria-required={ariaRequired}
      aria-label={ariaLabel}
      {...fieldAria(id, hint, error)}
      onChange={(e) => {
        setText(e.target.value);
        emit(parseNum(e.target.value));
      }}
      onBlur={() => {
        const n = parseNum(text);
        if (n != null) {
          const c = clamp(n);
          if (c !== n) emit(c);
          setText(format(c));
        }
      }}
    />
  );

  const withUnit =
    unit || prefix ? (
      <div className="input-group">
        {prefix && <span className="addon prefix">{prefix}</span>}
        {input}
        {unit && <span className="addon">{unit}</span>}
      </div>
    ) : (
      input
    );

  return (
    <Field label={label} hint={hint} error={error} className={className} htmlFor={id}>
      {stepper ? (
        <div className="stepper">
          <Button icon="minus" onClick={() => bump(-1)} disabled={disabled} aria-label={stepName ? `Decrease ${stepName}` : 'Decrease'} />
          <div className="grow">{withUnit}</div>
          <Button icon="plus" onClick={() => bump(1)} disabled={disabled} aria-label={stepName ? `Increase ${stepName}` : 'Increase'} />
        </div>
      ) : (
        withUnit
      )}
    </Field>
  );
}

export function Toggle({ checked, onChange, label, hint, disabled }: { checked: boolean; onChange: (v: boolean) => void; label?: ReactNode; hint?: ReactNode; disabled?: boolean }) {
  return (
    <label className="toggle" style={disabled ? { opacity: 0.5 } : undefined}>
      <input type="checkbox" role="switch" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="track" />
      {(label || hint) && (
        <span className="stack-sm" style={{ gap: 0 }}>
          {label && <span>{label}</span>}
          {hint && <span className="text-xs faint">{hint}</span>}
        </span>
      )}
    </label>
  );
}

/** Free-form tag input with optional suggestions (used for flavor notes / tasting notes). */
/** The suggestions a TagInput shows as chips to toggle; the rest are left out. */
export const shownTagSuggestions = (suggestions: readonly string[]) => suggestions.slice(0, 16);

export function TagInput({ label, value, onChange, suggestions = [], placeholder = 'Add…' }: { label?: ReactNode; value: string[]; onChange: (v: string[]) => void; suggestions?: string[]; placeholder?: string }) {
  const [text, setText] = useState('');
  const id = useId();
  const add = (t: string) => {
    const tag = t.trim();
    if (!tag) return;
    if (!value.some((v) => v.toLowerCase() === tag.toLowerCase())) onChange([...value, tag]);
    setText('');
  };
  const has = (t: string) => value.some((v) => v.toLowerCase() === t.toLowerCase());
  // Suggestions stay where they are and toggle in place, so tapping one doesn't reflow the chips under the finger.
  // Tags typed in (or beyond the suggestions shown) sit above the input.
  const shown = shownTagSuggestions(suggestions);
  const own = value.filter((v) => !shown.some((s) => s.toLowerCase() === v.toLowerCase()));
  return (
    <Field label={label} htmlFor={id}>
      <div className="stack-sm">
        {own.length > 0 && (
          <div className="row-wrap">
            {own.map((t) => (
              <button key={t} type="button" className="chip selected" onClick={() => onChange(value.filter((v) => v !== t))}>
                {t} <span className="x">×</span>
              </button>
            ))}
          </div>
        )}
        <input
          id={id}
          className="input"
          value={text}
          placeholder={placeholder}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              add(text);
            } else if (e.key === 'Backspace' && text === '' && value.length) {
              onChange(value.slice(0, -1));
            }
          }}
          onBlur={() => add(text)}
        />
        {shown.length > 0 && (
          <div className="row-wrap">
            {shown.map((s) => {
              const on = has(s);
              return (
                <button
                  key={s}
                  type="button"
                  className={cx('chip', on && 'selected')}
                  aria-pressed={on}
                  onClick={() => (on ? onChange(value.filter((v) => v.toLowerCase() !== s.toLowerCase())) : add(s))}
                >
                  <Icon name={on ? 'check' : 'plus'} size={14} />
                  {s}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Field>
  );
}

/**
 * Rating after activating `star` (1-5). A tap on the left half picks the half star, on the right half the whole
 * star; tapping the current value clears it. Keyboard activation (Enter/Space) has no position, so it cycles
 * the whole star -> half star -> cleared, which reaches every value.
 */
export function nextRating(current: number | null, star: number, via: 'left' | 'right' | 'keyboard'): number | null {
  if (via === 'keyboard') {
    if (current === star) return star - 0.5;
    if (current === star - 0.5) return null;
    return star;
  }
  const next = via === 'left' ? star - 0.5 : star;
  return next === current ? null : next;
}

/** 0-5 star rating in half steps. Click the same value again to clear. */
export function RatingInput({ value, onChange, size = 26, readOnly }: { value: number | null; onChange?: (v: number | null) => void; size?: number; readOnly?: boolean }) {
  const v = value ?? 0;
  return (
    <div className={cx('rating', readOnly && 'readonly')} role="group" aria-label={`Rating ${v} of 5`}>
      {[1, 2, 3, 4, 5].map((i) => {
        const full = v >= i;
        const half = !full && v >= i - 0.5;
        return (
          <button
            key={i}
            type="button"
            className={full || half ? 'on' : undefined}
            disabled={readOnly}
            aria-pressed={full ? true : half ? 'mixed' : false}
            onClick={(e) => {
              if (readOnly || !onChange) return;
              // detail is 0 for clicks synthesised from Enter/Space, whose clientX (0) says nothing about the half.
              if (e.detail === 0) return onChange(nextRating(value, i, 'keyboard'));
              const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
              onChange(nextRating(value, i, e.clientX - rect.left < rect.width / 2 ? 'left' : 'right'));
            }}
            aria-label={`${i} ${i === 1 ? 'star' : 'stars'}`}
          >
            <Icon name={half ? 'star-half' : 'star'} size={size} fill={full ? 'currentColor' : 'none'} />
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------- Modal
const ModalFooterContext = createContext<HTMLElement | null>(null);

let scrollLocks = 0;
/** Stops the page behind an open modal from scrolling (iOS scrolls it when the backdrop is dragged). */
export function useScrollLock(active: boolean) {
  useEffect(() => {
    if (!active) return;
    if (scrollLocks++ === 0) document.documentElement.classList.add('scroll-locked');
    return () => {
      if (--scrollLocks === 0) document.documentElement.classList.remove('scroll-locked');
    };
  }, [active]);
}

const FOCUSABLE = 'a[href], button:not(:disabled), input:not(:disabled):not([type="hidden"]), select:not(:disabled), textarea:not(:disabled), summary, [tabindex]:not([tabindex="-1"])';

/**
 * Keeps keyboard focus inside an open dialog: focus moves into it on open (unless a field in it took focus with
 * autoFocus), Tab and Shift+Tab wrap around inside it, and focus goes back to whatever opened it on close. Modal uses
 * it; a full-screen layer of a page's own (the bean photo viewer) can too: give its root `tabIndex={-1}` and
 * `role="dialog"`, and handle Escape itself.
 */
export function useModalFocus(open: boolean, dialogRef: { current: HTMLElement | null }) {
  // Read while rendering the open dialog: by the time effects run, an autoFocus field inside has already taken focus.
  const opener = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  if (open && !wasOpen.current && typeof document !== 'undefined') {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }
  wasOpen.current = open;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!open || !dialog) return;
    if (!dialog.contains(document.activeElement)) dialog.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.getClientRects().length > 0);
      const active = document.activeElement;
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) {
        e.preventDefault();
        return;
      }
      const outside = !dialog.contains(active);
      if (e.shiftKey && (outside || active === first || active === dialog)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (outside || active === last)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const back = opener.current;
      if (back?.isConnected) back.focus({ preventScroll: true });
    };
  }, [open, dialogRef]);
}

/**
 * Bottom sheet on phones, centred dialog from 700px. Only the body scrolls: `footer` stays pinned below it so the
 * Save button is always in reach. Content components that own the state their actions need can pin them with
 * <ModalFooter> instead.
 */
export function Modal({ open, onClose, title, children, footer }: { open: boolean; onClose: () => void; title?: ReactNode; children: ReactNode; footer?: ReactNode }) {
  const titleId = useId();
  const [footerEl, setFooterEl] = useState<HTMLDivElement | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  useScrollLock(open);
  useModalFocus(open, dialogRef);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby={title ? titleId : undefined} tabIndex={-1} ref={dialogRef}>
        <div className="modal-header">
          <h2 id={titleId}>{title}</h2>
          <Button variant="ghost" icon="x" onClick={onClose} aria-label="Close" />
        </div>
        <ModalFooterContext.Provider value={footerEl}>
          <div className="modal-body stack">{children}</div>
        </ModalFooterContext.Provider>
        {/* Hidden by CSS while empty. */}
        <div className="modal-footer form-actions" ref={setFooterEl}>
          {footer}
        </div>
      </div>
    </div>
  );
}

/** Renders its children in the enclosing Modal's pinned footer. */
export function ModalFooter({ children }: { children: ReactNode }) {
  const target = useContext(ModalFooterContext);
  // Inline until the footer element exists: on the first render, on the server and outside a Modal.
  return target ? createPortal(children, target) : <div className="form-actions">{children}</div>;
}

/**
 * Ask before throwing away edits in an editor modal. `request(action)` runs the action straight away when nothing
 * changed; otherwise `footer` turns the modal's footer into "Discard your changes?" with Keep editing and Discard.
 * Asking again while it shows (Escape, the close button) means keep editing.
 */
export function useConfirmDiscard(dirty: boolean) {
  const [pending, setPending] = useState<(() => void) | null>(null);
  const questionId = useId();
  const request = (action: () => void) => {
    if (pending) setPending(null);
    else if (dirty) setPending(() => action);
    else action();
  };
  // Keyed so its buttons mount fresh (autoFocus works) instead of reusing Cancel and Save's elements.
  const footer = pending && (
    <Fragment key="discard">
      <span className="grow discard-question" id={questionId}>
        Discard your changes?
      </span>
      <Button variant="ghost" autoFocus aria-describedby={questionId} onClick={() => setPending(null)}>
        Keep editing
      </Button>
      <Button
        variant="danger"
        icon="trash"
        onClick={() => {
          setPending(null);
          pending();
        }}
      >
        Discard
      </Button>
    </Fragment>
  );
  return { request, footer };
}

/** How long an armed ConfirmButton (and anything else that asks for a second tap) waits for it. */
export const CONFIRM_WINDOW_MS = 5000;

/**
 * Button for a destructive action: the first tap arms it, a second within CONFIRM_WINDOW_MS fires it. Armed, it
 * shows `confirmLabel` over its own label, so its width doesn't change: a short question ("Delete?"; by default the
 * label plus a question mark). Screen readers hear "Delete? Press again to confirm."
 */
export function ConfirmButton({ onConfirm, children, confirmLabel, className, ...rest }: ButtonProps & { onConfirm: () => void; confirmLabel?: string }) {
  const [armed, setArmed] = useState(false);
  const question = confirmLabel ?? (typeof children === 'string' ? `${children}?` : 'Confirm?');
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), CONFIRM_WINDOW_MS);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <Button
      {...rest}
      className={cx(className, armed && 'armed')}
      aria-label={armed ? question : rest['aria-label']}
      title={armed ? question : rest.title}
      onClick={() => {
        if (armed) {
          setArmed(false);
          onConfirm();
        } else {
          setArmed(true);
          announce(`${question}${/[.?!]$/.test(question) ? '' : '.'} Press again to confirm.`);
        }
      }}
    >
      {children != null ? (
        <>
          <span className="confirm-q" aria-hidden="true">
            <span>{question}</span>
          </span>
          {children}
        </>
      ) : undefined}
    </Button>
  );
}

// ---------------------------------------------------------------- ActionBar
export interface ActionBarAction {
  label: ReactNode;
  icon?: IconName;
  onClick?: () => void;
  /** 'submit' for the form the bar sits in. */
  type?: 'button' | 'submit';
  loading?: boolean;
  disabled?: boolean;
}

/**
 * The sticky bar with a page's main action (Save, Start brewing, Compare), above the bottom bar and in thumb reach.
 * The primary comes last; `children` are secondary actions before it (ghost buttons of the same `size`, a Toggle);
 * `status` says what is pending ("2 unsaved changes", "3 selected") or why the primary can't be used yet.
 *
 * The one dirty rule: a bar that saves edits passes `dirty`, and its primary stays disabled until something changed
 * ("No changes to save"). Leave `dirty` out where the action isn't saving edits, or where a new item can be saved as
 * it stands. Clean, the bar is one slim line (the status and a smaller disabled primary) and leaves out `children`,
 * which only act on edits (Cancel, Discard), so an idle bar covers little of the page.
 */
export function ActionBar({
  primary,
  dirty,
  status,
  children,
  size = 'lg',
  label,
  className,
}: {
  primary: ActionBarAction;
  dirty?: boolean;
  status?: ReactNode;
  children?: ReactNode;
  size?: 'md' | 'lg' | 'xl';
  /** Names the bar as a region for screen readers ("Compare selection"). */
  label?: string;
  className?: string;
}) {
  const clean = dirty === false;
  const shownStatus = status ?? (clean ? 'No changes to save' : null);
  const hasStatus = shownStatus != null && shownStatus !== false;
  return (
    <div className={cx('action-bar', clean && 'clean', hasStatus && 'with-status', className)} role={label ? 'region' : undefined} aria-label={label}>
      {hasStatus && <div className="action-bar-status">{shownStatus}</div>}
      <div className="action-bar-actions">
        {!clean && children}
        <Button
          variant="primary"
          size={clean ? 'md' : size}
          icon={primary.icon}
          type={primary.type ?? 'button'}
          loading={primary.loading}
          disabled={primary.disabled || clean}
          onClick={primary.onClick}
        >
          {primary.label}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Toasts
type ToastTone = 'info' | 'success' | 'error';
/** One button on the toast, e.g. Undo after archiving. A toast with an action stays up longer. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}
interface ToastItem { id: number; tone: ToastTone; message: ReactNode; action?: ToastAction; leaving?: boolean }
/** How long a dismissed toast takes to leave (the .toast[data-state='leaving'] transition). */
const TOAST_LEAVE_MS = 200;
const ToastContext = createContext<(message: ReactNode, tone?: ToastTone, action?: ToastAction) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);
  // Leaving first (it transitions out), then gone.
  const dismiss = useCallback((id: number) => {
    setItems((xs) => xs.map((x) => (x.id === id ? { ...x, leaving: true } : x)));
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), TOAST_LEAVE_MS);
  }, []);
  const push = useCallback(
    (message: ReactNode, tone: ToastTone = 'info', action?: ToastAction) => {
      const id = nextId.current++;
      setItems((xs) => [...xs, { id, tone, message, action }]);
      setTimeout(() => dismiss(id), action ? 8000 : tone === 'error' ? 6000 : 3000);
    },
    [dismiss],
  );
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toast-stack" aria-live="polite">
        {items.map((t) => (
          <Toast key={t.id} item={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/** One toast. It mounts off its edge and slides in on a transition, so a tap while it arrives reverses it smoothly. */
function Toast({ item: t, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [entered, setEntered] = useState(false);
  useLayoutEffect(() => {
    // Make the browser compute the "from" state before it changes, or there is nothing to transition from.
    void ref.current?.offsetHeight;
    setEntered(true);
  }, []);
  return (
    <div ref={ref} className={cx('toast', t.tone)} data-state={t.leaving ? 'leaving' : entered ? 'shown' : 'entering'} onClick={onDismiss}>
      {t.message}
      {t.action && (
        <Button
          size="sm"
          variant="ghost"
          className="toast-action"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
            t.action!.onClick();
          }}
        >
          {t.action.label}
        </Button>
      )}
    </div>
  );
}

/** const toast = useToast(); toast('Saved', 'success'); toast('Archived', 'success', { label: 'Undo', onClick: undo }) */
export function useToast() {
  return useContext(ToastContext);
}
